import React, { useMemo, useState, type FC } from "react";
import { MoreOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import { useNavigate } from "react-router-dom";
import { Button, Modal } from "../../../../components/shared";
import { usePamFeature } from "../../../../hooks/useFeatureFlag";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import { useCampaignTransition } from "../../../../queries/dynamicBonuses/useCampaignTransition";
import { useDuplicateCampaign } from "../../../../queries/dynamicBonuses/useDuplicateCampaign";
import { campaignClonePath } from "../clonePaths";
import type { DynamicBonusCampaignStatus } from "../types";

type CampaignRowAction = "duplicate" | "pause" | "expire";

// Mirrors the wizard's TRANSITIONS map: pause only applies to a running
// campaign; expire applies to anything still able to run. Duplicate is
// always available.
const PAUSABLE_STATUSES = new Set<DynamicBonusCampaignStatus>(["active"]);
const EXPIRABLE_STATUSES = new Set<DynamicBonusCampaignStatus>([
  "active",
  "paused",
]);

const CONFIRM_COPY: Record<
  CampaignRowAction,
  { title: string; body: string; okLabel: string; destructive: boolean }
> = {
  duplicate: {
    title: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_DUPLICATE_CONFIRM_TITLE,
    body: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_DUPLICATE_CONFIRM_BODY,
    okLabel: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_DUPLICATE,
    destructive: false,
  },
  pause: {
    title: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_PAUSE_CONFIRM_TITLE,
    body: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_PAUSE_CONFIRM_BODY,
    okLabel: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_PAUSE,
    destructive: false,
  },
  expire: {
    title: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_EXPIRE_CONFIRM_TITLE,
    body: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_EXPIRE_CONFIRM_BODY,
    okLabel: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_EXPIRE,
    destructive: true,
  },
};

type Props = {
  campaignId: string;
  name: string;
  status: DynamicBonusCampaignStatus;
};

/**
 * Per-row "⋯" menu on the campaigns list: Duplicate, Pause, and Expire.
 * When `dynamicBonusesDuplicateBuilder` is on, Duplicate opens the builder
 * with a clone preview and does not persist until Save. Flag-off keeps the
 * previous confirm + POST draft copy.
 */
const CampaignRowActions: FC<Props> = ({ campaignId, name, status }) => {
  const navigate = useNavigate();
  const { isEnabled: duplicateBuilderEnabled } = usePamFeature(
    "dynamicBonusesDuplicateBuilder",
  );
  const [pendingAction, setPendingAction] = useState<CampaignRowAction | null>(
    null,
  );
  const transition = useCampaignTransition(campaignId);
  const duplicate = useDuplicateCampaign();
  const isMutating = transition.isPending || duplicate.isPending;

  const menuItems = useMemo<MenuProps["items"]>(
    () => [
      {
        key: "duplicate",
        label: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_DUPLICATE,
      },
      {
        key: "pause",
        label: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_PAUSE,
        disabled: !PAUSABLE_STATUSES.has(status),
      },
      {
        key: "expire",
        label: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_EXPIRE,
        danger: true,
        disabled: !EXPIRABLE_STATUSES.has(status),
      },
    ],
    [status],
  );

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === "duplicate" && duplicateBuilderEnabled) {
      navigate(campaignClonePath(campaignId));
      return;
    }
    setPendingAction(key as CampaignRowAction);
  };

  const handleConfirm = () => {
    if (!pendingAction) return;
    const close = { onSettled: () => setPendingAction(null) };
    if (pendingAction === "duplicate") {
      duplicate.mutate(campaignId, close);
    } else {
      transition.mutate(pendingAction, close);
    }
  };

  const confirmCopy = pendingAction ? CONFIRM_COPY[pendingAction] : null;

  return (
    // The whole row navigates to the campaign detail on click — keep menu
    // and modal interactions from bubbling into that navigation.
    /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
    <div onClick={(event) => event.stopPropagation()}>
      <Dropdown
        menu={{
          items: menuItems,
          onClick: handleMenuClick,
        }}
        trigger={["click"]}
      >
        <Button
          intent="neutral"
          variant="ghost"
          iconLeft={<MoreOutlined />}
          aria-label={AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTIONS_LABEL}
          data-testid={`campaign-actions-${campaignId}`}
        />
      </Dropdown>

      {confirmCopy && (
        <Modal
          open
          size="sm"
          title={confirmCopy.title}
          intent={confirmCopy.destructive ? "destructive" : "default"}
          onClose={() => setPendingAction(null)}
          primaryAction={{
            label: confirmCopy.okLabel,
            onClick: handleConfirm,
            loading: isMutating,
          }}
          secondaryAction={{
            label: AppStrings.CANCEL,
            onClick: () => setPendingAction(null),
            disabled: isMutating,
          }}
        >
          {formatString(confirmCopy.body, { name })}
        </Modal>
      )}
    </div>
  );
};

export default CampaignRowActions;
